/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid, makeStyles, MenuItem, Select, Typography } from '@material-ui/core';
import { MailOutline } from '@material-ui/icons';

import { clearScrollLocks, disableScroll, enableScroll } from '../../utils/scroll';
import AdditionalFilesList from '../additionalFiles/AdditionalFilesList';
import EditFields from '../common/EditFields';
import ImageWrapper from '../common/ImageWrapper';
import VideoWrapper from '../common/VideoWrapper';
import ObjViewer from '../objViewer/ObjViewer';
import PdfViewer from '../pdfViewer/PdfViewer';
import PersonalizedVideoOnlinePortalAccess from '../personalizedVideoOnlinePortalAccess/PersonalizedVideoOnlinePortalAccess';

const useStyles = makeStyles(() => {
  return {
    additionalFilesTitle: {
      fontSize: '18px !important',
    },
    additionalFilesViewButton: {
      fontSize: '14px !important',
    },
    classFieldContainer: {
      margin: '16px 0px',
    },
    classFieldLabel: {
      marginBottom: '8px',
    },
    container: {
      justify: 'space-between',
    },
    mailMessage: {
      aligItems: 'center',
      backgroundColor: '#f3f3f3',
      display: 'flex',
      gap: '8px',
      padding: '20px',
    },
    sectionContainer: {
      marginBottom: '1rem',
      textAlign: 'left',
    },
    sectionText: {
      color: '#999999',
      fontSize: '14px',
      maxHeight: '200px',
      overflowY: 'auto',
      paddingRight: '1rem',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    sectionTitle: {
      color: '#172B4D',
      fontSize: '18px',
      fontWeight: 'bold',
    },
    sharedDetailsContainer: {
      margin: '0 0px 20px',
    },
    sharedDetailsTitle: {
      color: '#172B4D',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    strongDescription: {
      color: '#999999',
      fontSize: '17px',
      fontWeight: '400',
    },
  };
});

export default ({
  auth,
  caseStudy,
  reportSubmitted,
  childResources,
  onViewAdditionalFile,
  onUpdateUploadSharedInfo,
  uploadSharedInfo,
  onViewAdditionalFiles,
  onlinePortalAccess,
}) => {
  const classes = useStyles();
  const [state, setstate] = useState({ caseStudy, reportSubmitted });
  const [selectedResource, setSelectedResource] = useState(null);
  const ref = useRef();

  const sharedDetailsFields = useMemo(
    () => [
      {
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Online Portal Address',
        name: 'portalAddress',
        placeholder: 'app.mediphany.com',
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
      {
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Portal Username',
        name: 'portalUsername',
        placeholder: '',
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
      {
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Portal Password',
        name: 'portalPassword',
        placeholder: '',
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
      {
        className: 'custom-height',
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Study Name',
        multiline: true,
        name: 'studyName',
        placeholder: '',
        rows: 3,
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const sharedLinkFields = useMemo(
    () => [
      {
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Folder Url',
        name: 'sharingLink',
        placeholder: 'app.mediphany.com',
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
      {
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Service Name',
        name: 'onlineServiceName',
        placeholder: '',
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
      {
        className: 'custom-height',
        containerClass: classes.classFieldContainer,
        disabled: true,
        label: 'Study Name',
        name: 'studyDescription',
        placeholder: '',
        size: 'small',
        type: 'text',
        variant: 'outlined',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (ref.current) {
      ref.current?.addEventListener('mouseenter', handleMouseEnter);
      ref.current?.addEventListener('mouseleave', handleMouseLeave);
    }
    return () => {
      if (ref.current) {
        ref.current?.removeEventListener('mouseenter', handleMouseEnter);
        ref.current?.removeEventListener('mouseleave', handleMouseLeave);
        clearScrollLocks();
      }
    };
  }, []);

  useEffect(() => {
    setstate({ caseStudy, reportSubmitted });
  }, [caseStudy, reportSubmitted]);

  const handleMouseEnter = () => {
    disableScroll(ref.current);
  };

  const handleMouseLeave = () => {
    enableScroll(ref.current);
  };

  const handleViewAdditionalFile = (file) => {
    if (onViewAdditionalFile) onViewAdditionalFile(file);
  };

  const handleViewAdditionalFiles = () => {
    if (onViewAdditionalFiles) onViewAdditionalFiles();
  };

  const handleScanDetailsChange = useCallback((e) => {
    if (e?.target?.value === 'main-resource') {
      setSelectedResource(null);
      return;
    }

    setSelectedResource(e?.target?.value);
  }, []);

  const renderWrittenReport = () => {
    // MED-595: show this only for patient
    if (auth?.role !== 'patient') {
      return null;
    }

    return (
      state.reportSubmitted && (
        <Grid container item className={classes.sectionContainer} direction="column">
          <Grid item style={{ marginBottom: '1rem' }}>
            <Typography className={classes.sectionTitle}> Written Report</Typography>
          </Grid>
          <Grid item>
            <Typography className={classes.sectionText}>{state.caseStudy?.workspace?.globalVideoIntroduction} What is going on</Typography>
          </Grid>
        </Grid>
      )
    );
  };

  const renderPatientQuestion = () => (
    <Grid container direction="column" style={{ marginBottom: '1rem', marginTop: '16px', minHeight: '100px' }}>
      <Grid item>
        <Typography className={classes.sectionTitle}>{state.caseStudy?.isLawFirmCase ? 'Relevant Information' : 'Patient Question'}</Typography>
      </Grid>
      <Grid item>
        <Typography className={classes.sectionText}>{state.caseStudy.questionText}</Typography>
      </Grid>
    </Grid>
  );

  const renderArgument = () => (
    <Grid item xs={12} className="mt-2">
      <Grid container direction="column" style={{ marginBottom: '1rem', marginTop: '16px', minHeight: '100px' }}>
        <Grid item>
          <Typography className={classes.sectionTitle}>Expected Argument</Typography>
        </Grid>
        <Grid item>
          <Typography className={classes.sectionText}>{state.caseStudy.patient?.argument}</Typography>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderImageView = () => (
    <div style={{ minHeight: '30px' }}>
      {childResources?.length > 0 ? (
        <>
          <Typography className={classes.sectionTitle}>Scan Details</Typography>

          <div style={{ padding: '4px 0' }}>
            <Select
              autoWidth
              value={selectedResource || 'main-resource'}
              onChange={handleScanDetailsChange}
              style={{ border: '1px solid #CCC', borderRadius: '4px', maxWidth: '320px', minWidth: '100px', padding: '0px 8px', width: '100%' }}>
              <MenuItem value="main-resource">{(state.caseStudy?.dicomStudy?.description || 'MAIN IMAGE').toUpperCase()} #1</MenuItem>
              {childResources.map((resource, idx) => (
                <MenuItem value={resource} key={`${resource.id}--menu-item`}>
                  {(resource.description || 'No Title').toUpperCase()} #{idx + 2}
                </MenuItem>
              ))}
            </Select>
          </div>
        </>
      ) : null}

      {state?.caseStudy?.viewerLink && (
        <div style={{ color: 'white', height: '470px', overflow: 'auto', paddingRight: '1rem' }} ref={ref}>
          {state.caseStudy?.studyFileType === '3d' ? (
            <ObjViewer dimensions={{ height: '70vh', width: '100%' }} url={state.caseStudy.viewerLink} uuid="client-data-uuid" shouldUpdateHash />
          ) : null}

          {state.caseStudy?.studyFileType === 'video' ? <VideoWrapper url={state.caseStudy.viewerLink} isPlayOnClick isStaticSize /> : null}

          {state.caseStudy?.studyFileType === 'pdf' ? <PdfViewer hideToolbar url={state.caseStudy.viewerLink} dimensions={{ height: '100vh', width: '100%' }} /> : null}

          {state.caseStudy?.studyFileType === 'image' ? <ImageWrapper url={state.caseStudy.viewerLink} height="70vh" /> : null}

          {state.caseStudy?.studyFileType === 'dcm' ? (
            <iframe
              key={selectedResource?.id || 'dcm-resource-detail'}
              title="Client Data"
              style={{ border: 'none', height: '100%', width: '100%' }}
              src={selectedResource?.viewerLink || state?.caseStudy?.viewerLink}
            />
          ) : null}
        </div>
      )}

      {state.caseStudy?.dcmRequestType === 'UserRequestedImagelessVideoReport' && (
        <Typography className={classes.sectionText}>Client was unable to find/get their radiology images.</Typography>
      )}
    </div>
  );

  const renderPendingUploadView = () => (
    <Grid container className="text-left">
      <Grid item xs={12}>
        <Typography className="secondary-header">Scan Details</Typography>
        <Typography className={`mt-1 ${classes.strongDescription}`}>The scan details will not be available until the study is uploaded.</Typography>
      </Grid>
      <Grid item xs={12} className="mt-2">
        {renderPatientQuestion()}
      </Grid>
      <Grid item xs={12}>
        <Typography className="secondary-header">Clinic Upload Requirements</Typography>
        <Typography className={`mt-1 ${classes.strongDescription}`}>
          {caseStudy.clinicUploadOption}{' '}
          <span>
            <br />
            {caseStudy.clinicUploadDetail}
          </span>
        </Typography>
      </Grid>
    </Grid>
  );

  const renderSharedDetails = () => {
    if (onlinePortalAccess) {
      return (
        <Grid container className={`text-left ${classes.sharedDetailsContainer}`}>
          <PersonalizedVideoOnlinePortalAccess status={{ formOnlinePortalAccess: uploadSharedInfo }} onChangeFormOnlinePortalAccess={onUpdateUploadSharedInfo} isAdminView />
        </Grid>
      );
    }

    const caseData = caseStudy.caseLinkInfo ? caseStudy.caseLinkInfo : caseStudy.uploadSharedInfo;

    return (
      <Grid container className={`text-left ${classes.sharedDetailsContainer}`}>
        <Grid item xs={12}>
          <Typography className={classes.sharedDetailsTitle}>Shared Details:</Typography>
        </Grid>
        <Grid item xs={12} md={10} lg={8} xl={6}>
          <EditFields labelClass={classes.classFieldLabel} data={caseData} useLabel labelPosition="top" fields={caseStudy.caseLinkInfo ? sharedLinkFields : sharedDetailsFields} />
        </Grid>
      </Grid>
    );
  };

  const renderAdditionalFiles = () => {
    const managedDetails = { case: state.caseStudy };
    const customClasses = {
      title: classes.additionalFilesTitle,
      viewButton: classes.additionalFilesViewButton,
      viewMoreButton: classes.additionalFilesViewButton,
    };

    return (
      <AdditionalFilesList
        onViewAdditionalFile={handleViewAdditionalFile}
        onViewMoreAdditionalFiles={handleViewAdditionalFiles}
        caseDetails={managedDetails}
        customClasses={customClasses}
        shouldItemsFullWidth
      />
    );
  };

  const mainComponentRender = caseStudy?.status?.value === 'pending-upload' ? renderPendingUploadView() : renderWrittenReport();
  const additionalComponentRender = caseStudy.status?.value !== 'pending-upload' && (
    <Grid item className={classes.sectionContainer}>
      {state.reportSubmitted ? renderPatientQuestion() : renderImageView()}
      {state.reportSubmitted ? renderImageView() : renderPatientQuestion()}

      {state.caseStudy?.isLawFirmCase && renderArgument()}
    </Grid>
  );
  const sharedDetailComponentRender = caseStudy?.caseLinkInfo || caseStudy?.uploadSharedInfo || onlinePortalAccess ? renderSharedDetails() : false;
  const isMailCase = caseStudy?.status?.value === 'pending-images' && !caseStudy.caseLinkInfo;

  return (
    <Grid container className={classes.container}>
      {isMailCase && (
        <Grid item xs={12}>
          <div className={classes.mailMessage}>
            <MailOutline style={{ color: '#999' }} /> This case is waiting for images sent by mail. Once Mediphany get them, they will be uploaded to the case.
          </div>
        </Grid>
      )}
      <Grid item xs={12}>
        {mainComponentRender}
      </Grid>
      <Grid item xs={12}>
        {additionalComponentRender}
      </Grid>
      <Grid item xs={12}>
        {sharedDetailComponentRender}
      </Grid>
      <Grid item xs={12}>
        {renderAdditionalFiles()}
      </Grid>
    </Grid>
  );
};
